import { useMemo, useState } from 'react';
import { useAssignCourse, useCourses, useDepartments, useEmployees } from '../../api/hooks';
import { useT } from '../../i18n/LangProvider';
import { Icon } from '../../ui/Icon';

type Target = 'people' | 'departments' | 'everyone';

export function AssignModal({ presetCourseId, onClose, onToast }: {
  presetCourseId: string | null;
  onClose: () => void;
  onToast: (m: string) => void;
}) {
  const tr = useT();
  const coursesQ = useCourses();
  const employeesQ = useEmployees();
  const deptsQ = useDepartments();
  const assign = useAssignCourse();

  const courses = coursesQ.data ?? [];
  const employees = (employeesQ.data?.items ?? []).filter((e) => e.active);
  const depts = deptsQ.data ?? [];

  const [courseId, setCourseId] = useState(presetCourseId ?? '');
  const [target, setTarget] = useState<Target>('people');
  const [people, setPeople] = useState<string[]>([]);
  const [deptIds, setDeptIds] = useState<string[]>([]);
  const [dueAt, setDueAt] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? employees.filter((e) => e.name.toLowerCase().includes(t) || e.email.toLowerCase().includes(t)) : employees;
  }, [employees, search]);

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const recipients = target === 'everyone'
    ? employees.length
    : target === 'departments'
      ? employees.filter((e) => e.departmentId && deptIds.includes(e.departmentId)).length
      : people.length;

  async function send() {
    setError(null);
    if (!courseId) { setError(tr('Scegli un corso.')); return; }
    if (recipients === 0) { setError(tr('Seleziona almeno un destinatario.')); return; }
    try {
      const res = await assign.mutateAsync({
        courseId,
        ...(target === 'people' ? { employeeIds: people } : {}),
        ...(target === 'departments' ? { departmentIds: deptIds } : {}),
        ...(target === 'everyone' ? { everyone: true } : {}),
        ...(dueAt ? { dueAt: new Date(`${dueAt}T23:59:59`).toISOString() } : {}),
      });
      onToast(tr(res.assigned === 1 ? 'Corso assegnato a {n} persona' : 'Corso assegnato a {n} persone', { n: res.assigned }));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('Assegnazione non riuscita.'));
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lrn-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lrn-modal__head">
          <h3><Icon name="send" size={16} /> {tr('Invia formazione')}</h3>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={13} /></button>
        </div>

        <div className="lrn-modal__body">
          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Corso')}</span>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
              <option value="">{tr('— scegli un corso —')}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}{c.published ? '' : tr(' (bozza)')}{c.mandatory ? tr(' · obbligatorio') : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="lrn-field">
            <span className="lrn-field__lbl">{tr('Destinatari')}</span>
            <div className="lrn-chips">
              {([['people', 'Persone'], ['departments', 'Reparti'], ['everyone', 'Tutta l’azienda']] as const).map(([id, label]) => (
                <button key={id} className={`chip ${target === id ? 'is-active' : ''}`} onClick={() => setTarget(id)}>
                  {tr(label)}
                </button>
              ))}
            </div>
          </div>

          {target === 'people' && (
            <div className="lrn-field">
              <div className="lrn-search">
                <Icon name="search" size={13} />
                <input placeholder={tr('Cerca persona…')} value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="lrn-people-grid">
                {filtered.map((e) => (
                  <button key={e.id} className={`lrn-person ${people.includes(e.id) ? 'is-on' : ''}`}
                    onClick={() => setPeople((p) => toggle(p, e.id))}>
                    <span className="lrn-avatar is-neutral">{e.initials ?? e.name.slice(0, 2).toUpperCase()}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {target === 'departments' && (
            <div className="lrn-field">
              <div className="lrn-chips">
                {depts.map((d) => (
                  <button key={d.id} className={`chip ${deptIds.includes(d.id) ? 'is-active' : ''}`}
                    onClick={() => setDeptIds((x) => toggle(x, d.id))}>
                    {d.name}
                    <span className="lrn-chips__n">
                      {employees.filter((e) => e.departmentId === d.id).length}
                    </span>
                  </button>
                ))}
              </div>
              {depts.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun reparto configurato.')}</div>}
            </div>
          )}

          {target === 'everyone' && (
            <div className="muted" style={{ fontSize: 13 }}>
              {tr('Il corso sarà assegnato a tutti i {n} utenti attivi.', { n: employees.length })}
            </div>
          )}

          <div className="lrn-field lrn-field--inline">
            <span className="lrn-field__lbl">{tr('Scadenza (opz.)')}</span>
            <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </div>

          {error && <div className="lrn-quiz__result is-fail" style={{ borderRadius: 8 }}><Icon name="warn" size={14} /> {error}</div>}
        </div>

        <div className="lrn-modal__foot">
          <span className="muted" style={{ fontSize: 12.5 }}>
            {tr(recipients === 1 ? '{n} destinatario' : '{n} destinatari', { n: recipients })}
          </span>
          <span className="cluster">
            <button className="btn btn--sm" onClick={onClose}>{tr('Annulla')}</button>
            <button className="btn btn--sm btn--brand" disabled={assign.isPending} onClick={() => void send()}>
              {assign.isPending ? tr('Invio…') : tr('Assegna')}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
